import { useQueryGetAllBots } from "@apis:bot/bot";
import { useMutationTranslateDocument } from "@apis:translation/document";
import { useBranding } from "@app-setup:BrandingContext";
import { ActionButton } from "@components:ActionButton";
import { BrandedLogo } from "@components:BrandedLogo";
import { Header } from "@components:Header";
import { SwitchPage } from "@components:SwitchPage";
import { useBotSelection } from "@hooks:useBotSelection";
import { apiErrorDetail } from "@libs:axios";
import { BotSelectionPersistent } from "@modules:BotSelection/BotSelection";
import { Box } from "@mui/material";
import { TranslationHeader } from "@pages:app/document-translation/components/TranslationHeader";
import { translationReducer } from "@pages:app/document-translation/utils/stateManagement";
import { useToasterStoreActions } from "@stores:global";
import FileSaver from "file-saver";
import { useReducer, useState } from "react";
import { useTranslation } from "react-i18next";

import { AppType } from "@:constants";

import { DropZone } from "./components/DropZone";
import { ErrorCard } from "./components/ErrorCard";
import { LanguageSelector } from "./components/LanguageSelector";
import { LoadingState } from "./components/LoadingState";
import { SuccessCard } from "./components/SuccessCard";
import {
  generateTranslatedFilename,
  getDocumentTypeLabel,
  getFileKind,
  validateFile,
} from "./utils/fileHelpers";

export const DocumentTranslation = () => {
  const { t } = useTranslation();
  const toastActions = useToasterStoreActions();
  const { getPageConfig } = useBranding();
  const translationConfig = getPageConfig("translation");
  const [state, dispatch] = useReducer(translationReducer, { status: "idle" });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [targetLanguage, setTargetLanguage] = useState<string>();

  const { selectedBotId } = useBotSelection();
  const botId = selectedBotId || "";
  const { data: bots } = useQueryGetAllBots(true, AppType.Translation);
  const documentTranslationConfig = bots?.find((bot) => bot.id === botId)
    ?.ai_settings.document_translation;
  const maxFileSizeMB = documentTranslationConfig?.max_file_size_mb ?? 10;

  const mutation = useMutationTranslateDocument(botId, {
    onSuccess: (response) => {
      if (state.status !== "translating") return;
      // Use server-provided filename if available, otherwise generate client-side
      const filename =
        response.filename ||
        generateTranslatedFilename(state.filename, state.language);
      dispatch({ type: "TRANSLATION_SUCCESS", blob: response.blob, filename });
    },
    onError: (error) => {
      dispatch({
        type: "TRANSLATION_ERROR",
        message:
          apiErrorDetail(error) ||
          error.message ||
          t("translation.errors.translationFailed"),
      });
    },
  });

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    mutation.reset();
  };

  const handleSubmit = () => {
    if (
      !selectedFile ||
      !botId ||
      !targetLanguage ||
      !documentTranslationConfig
    )
      return;

    const validation = validateFile(selectedFile, maxFileSizeMB, t);
    if (!validation.valid) {
      toastActions.setToaster({
        variant: "error",
        message: validation.error || t("translation.errors.invalidFile"),
        open: true,
      });
      return;
    }

    dispatch({
      type: "START_TRANSLATION",
      file: selectedFile,
      language: targetLanguage,
    });

    mutation.mutate({ file: selectedFile, targetLanguage });
  };

  const handleDownload = () => {
    if (state.status !== "success") return;
    FileSaver.saveAs(state.translatedFile.blob, state.translatedFile.filename);
    dispatch({ type: "DOWNLOAD" });
  };

  const handleReset = () => {
    setSelectedFile(null);
    setTargetLanguage(undefined);
    mutation.reset();
    dispatch({ type: "RESET" });
  };

  const handleRetry = () => {
    if (state.status !== "error") return;
    if (!selectedFile || !botId || !targetLanguage) {
      handleReset();
      return;
    }

    handleSubmit();
  };

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.background.default,
        minHeight: "100vh",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "30px",
          background: (theme) => theme.palette.common.white,
          flexGrow: 1,
        }}
      >
        <Header
          sx={{
            bgcolor: (theme) => theme.palette.common.white,
            borderTopLeftRadius: 30,
            borderTopRightRadius: 30,
          }}
          leftSide={
            <Box sx={{ ml: 3 }}>
              <BrandedLogo />
            </Box>
          }
          rightSide={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ px: 1 }}>
                <BotSelectionPersistent app={AppType.Translation} />
              </Box>
              <SwitchPage />
            </Box>
          }
        />

        <Box
          key={botId}
          sx={{ flex: 1, textAlign: "center", pt: "100px", px: 2 }}
        >
          <TranslationHeader
            status={state.status}
            pageConfig={translationConfig}
          />

          {/* Idle State - Upload Zone */}
          {state.status === "idle" && (
            <Box
              sx={{
                maxWidth: "600px",
                mx: "auto",
                mt: 6,
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <DropZone
                onFileSelect={handleFileSelect}
                accept=".docx,.pptx"
                maxSizeMB={maxFileSizeMB}
                disabled={!documentTranslationConfig}
                selectedFile={selectedFile}
                hint={t("translation.dropzone.hint")}
              />

              <LanguageSelector
                value={targetLanguage ?? ""}
                onChange={(value) => {
                  setTargetLanguage(value);
                  mutation.reset();
                }}
              />

              <ActionButton
                disabled={
                  !selectedFile ||
                  !botId ||
                  !targetLanguage ||
                  !documentTranslationConfig
                }
                onClick={handleSubmit}
              >
                {t("translation.translateAction", {
                  type: t(
                    getDocumentTypeLabel(getFileKind(selectedFile?.name)),
                  ),
                })}
              </ActionButton>
            </Box>
          )}

          {/* Translating State */}
          {state.status === "translating" && (
            <LoadingState
              filename={state.filename}
              language={targetLanguage}
              size={state?.file?.size}
            />
          )}

          {/* Success State */}
          {state.status === "success" && (
            <SuccessCard
              translatedFile={state.translatedFile}
              onDownload={handleDownload}
              onTranslateAnother={handleReset}
            />
          )}

          {/* Error State */}
          {state.status === "error" && (
            <ErrorCard
              filename={state.filename}
              size={state.fileSize}
              onRetry={handleRetry}
              onUploadDifferent={handleReset}
              language={targetLanguage}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
